import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ArrowDown, ArrowUpRight, ChevronRight, Instagram, Menu, X } from "lucide-react";
import "@/App.css";
import { BrandMark } from "@/components/BrandMark";
import { SectionLabel } from "@/components/SectionLabel";
import CocktailVaultNext from "@/components/CocktailVaultNext";
import { BriefDrawer } from "@/components/BriefDrawer";
import EventCalculator from "@/components/EventCalculator";
import BookingForm from "@/components/BookingForm";
import ImmersiveJourney from "@/components/ImmersiveJourney";
import PricingShowcase from "@/components/PricingShowcase";
import FaqSection from "@/components/FaqSection";
import TeamDashboard from "@/components/TeamDashboard";
import PayDeposit from "@/components/PayDeposit";
import PaymentSuccess from "@/components/PaymentSuccess";
import { HERO_VIDEO, HERO_VIDEO_MOBILE, HERO_VIDEO_WEBM, MEDIA, chapters, marqueeItems, services, testimonials } from "@/siteData";
import { cocktailMenu } from "@/craftCocktails";

function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "240px 0px" });
    const observeAll = () => document.querySelectorAll(".reveal-on-scroll:not(.is-visible)").forEach((item) => observer.observe(item));
    observeAll();
    const mutations = new MutationObserver(observeAll);
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);
}

function Hero({ onExplore }) {
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const moveImage = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setParallax({ x: (event.clientX - rect.left - rect.width / 2) / 34, y: (event.clientY - rect.top - rect.height / 2) / 34 });
  };
  return (
    <section className="hero" id="top" onPointerMove={moveImage} onPointerLeave={() => setParallax({ x: 0, y: 0 })} data-testid="hero-section-container">
      <div className="hero-video-bg" aria-hidden="true">
        <video className="hero-video" autoPlay muted loop playsInline preload="metadata" poster={MEDIA.hero} data-testid="hero-drone-video">
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
          <source src={window.innerWidth < 768 ? HERO_VIDEO_MOBILE : HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="hero-video-overlay" />
      </div>
      <div className="hero-copy">
        <div className="eyebrow hero-eyebrow" data-testid="hero-region-badge"><span className="eyebrow-dot" /> South Florida · Miami · Broward · Palm Beach</div>
        <h1 data-testid="hero-main-headline">
          <span className="hero-line-mask"><span className="hero-line">Bespoke mixology.</span></span>
          <span className="hero-line-mask"><span className="hero-line">Artisanal grazing.</span></span>
          <span className="hero-line-mask"><span className="hero-line">Unrivaled presence.</span></span>
        </h1>
        <p className="hero-lead" data-testid="hero-lead">Pop Social turns private affairs into cinematic hospitality moments — with handcrafted cocktails, white-glove bartenders, and grazing tables that become the room’s centrepiece.</p>
        <div className="hero-actions">
          <a className="button button-primary" href="#calculator" data-testid="hero-quote-calculator-cta">Calculate your event quote <ArrowUpRight size={16} /></a>
          <a className="button button-quiet" href="#cocktails" data-testid="hero-cocktail-vault-cta">Enter the cocktail vault <ChevronRight size={16} /></a>
        </div>
        <div className="hero-footnote"><span>01</span><span>Hospitality, edited</span><span>Scroll to explore <ArrowDown size={14} /></span></div>
      </div>
      <div className="hero-visual">
        <figure className="hero-photo-frame">
          <img className="hero-photo" src={MEDIA.hero} alt="Amber cocktail being poured into a crystal glass" style={{ transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0) scale(1.04)` }} />
          <figcaption className="hero-caption"><span>01 / 07</span><span>Liquid ritual · precision pour</span></figcaption>
        </figure>
        <div className="hero-visual-meta"><span className="hero-visual-line" /><span>Designed for the kind of night people retell</span></div>
      </div>
      <div className="hero-stats" data-testid="hero-stats-grid">
        <div><strong>01</strong><span>Foundation experience</span></div>
        <div><strong>50</strong><span>Guests per bartender</span></div>
        <div><strong>58</strong><span>Craft cocktail expressions</span></div>
      </div>
    </section>
  );
}

function Marquee() {
  const track = [...marqueeItems, ...marqueeItems];
  return <section className="marquee-section" aria-label="Pop Social services" data-testid="editorial-marquee-container"><div className="marquee-window"><div className="marquee-track" data-testid="editorial-marquee-track">{track.map((item, index) => <span key={`${item}-${index}`}>{item}<b>✦</b></span>)}</div></div></section>;
}

function Manifesto() {
  return (
    <section className="section manifesto-section" id="manifesto" data-testid="manifesto-section-container">
      <div className="section-intro reveal-on-scroll">
        <SectionLabel number="02" testId="manifesto-section-label">The Pop Social standard</SectionLabel>
        <div className="intro-grid"><h2 data-testid="manifesto-heading">The details are the destination.</h2><p data-testid="manifesto-description">A good bar serves drinks. A Pop Social bar gives the evening a visual language — one that moves from first impression to final garnish without losing the plot.</p></div>
      </div>
      <div className="manifesto-layout">
        <figure className="manifesto-photo reveal-on-scroll"><img src={MEDIA.bartender} alt="Bartender garnishing a coupe cocktail" /><figcaption><span>Craft in motion</span><span>Every pour has a point of view.</span></figcaption></figure>
        <div className="chapter-list">
          {chapters.map((chapter, index) => <article className="chapter-card reveal-on-scroll" style={{ "--reveal-delay": `${index * 100}ms` }} key={chapter.number} data-testid={`manifesto-chapter-card-${chapter.number}`}><div className="chapter-number">{chapter.number}</div><div><span className="card-eyebrow">{chapter.tagline}</span><h3>{chapter.title}</h3><p>{chapter.body}</p><strong>{chapter.detail}</strong></div></article>)}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="section services-section" id="services" data-testid="services-section-container">
      <div className="section-intro reveal-on-scroll"><SectionLabel number="04" testId="services-section-label">The service edit</SectionLabel><div className="intro-grid"><h2 data-testid="services-heading">A complete evening, composed in layers.</h2><p data-testid="services-description">Start with the foundation, then build the kind of atmosphere your guest list deserves — from custom menus to a charcuterie cart that keeps the conversation moving.</p></div></div>
      <div className="services-grid">
        {services.map((service, index) => <article className={`service-card service-card-${service.id} reveal-on-scroll`} style={{ "--reveal-delay": `${index * 80}ms` }} key={service.id} data-testid={`package-card-${service.id}`}><div className="service-card-top"><span className="card-eyebrow">{service.eyebrow}</span><span className="service-price">{service.price}</span></div><h3>{service.title}</h3><p>{service.body}</p><ul>{service.items.map((item) => <li key={item}><span>✦</span>{item}</li>)}</ul><a href="#reserve" className="text-button" data-testid={`service-quote-link-${service.id}`}>Build this into my brief <ArrowUpRight size={15} /></a></article>)}
        <figure className="service-image-card reveal-on-scroll"><img src={MEDIA.grazing} alt="Artisanal grazing table with cheeses and fruit" /><figcaption><span>05 / The edible centrepiece</span><strong>Let the table do the talking.</strong><a href="#reserve" data-testid="charcuterie-service-link">Explore charcuterie service <ArrowUpRight size={15} /></a></figcaption></figure>
      </div>
    </section>
  );
}

function Proof() {
  const [reviews, setReviews] = useState(testimonials);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`)
      .then((response) => response.json())
      .then((payload) => { if (payload.testimonials?.length) setReviews(payload.testimonials); })
      .catch(() => {});
  }, []);
  return (
    <section className="section proof-section" id="proof" data-testid="client-reviews-container">
      <div className="section-intro reveal-on-scroll"><SectionLabel number="08" testId="reviews-section-label">A few words from the room</SectionLabel><div className="intro-grid"><h2 data-testid="reviews-heading">The kind of service guests notice.</h2><p data-testid="reviews-description">Real reviews from real rooms — the things people mention on the drive home.</p></div></div>
      <div className="proof-grid">{reviews.map((review, index) => <article className="review-card reveal-on-scroll" style={{ "--reveal-delay": `${index * 100}ms` }} key={review.id || review.author} data-testid={`review-card-${index + 1}`}><span className="review-mark">“</span>{review.rating && <span className="review-stars" aria-label={`${review.rating} star Google review`}>★★★★★</span>}<p className={review.quote.length > 200 ? "is-long" : ""}>{review.quote}</p><div className="review-credit"><strong>{review.author}</strong><span>{review.event}</span></div></article>)}</div>
    </section>
  );
}

function HomePage() {
  useEffect(() => {
    const imageFallback = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 800'%3E%3Crect width='1200' height='800' fill='%2315110e'/%3E%3Cpath d='M0 600L350 240l180 180 170-190 500 570H0z' fill='%23271e17'/%3E%3Ccircle cx='890' cy='245' r='90' fill='none' stroke='%23d49b4b' stroke-width='6' opacity='.7'/%3E%3Cpath d='M760 245h260M890 115v260' stroke='%23d49b4b' stroke-width='3' opacity='.55'/%3E%3C/svg%3E";
    const repairImage = (event) => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement) || image.dataset.fallbackApplied === "true") return;
      image.dataset.fallbackApplied = "true";
      image.src = imageFallback;
    };
    document.addEventListener("error", repairImage, true);
    return () => document.removeEventListener("error", repairImage, true);
  }, []);
  useReveal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedCocktailIds, setSelectedCocktailIds] = useState([]);
  const [calcBrief, setCalcBrief] = useState(null);
  const handleCalcChange = useCallback((next) => setCalcBrief(next), []);
  const selectedCocktailNames = useMemo(() => cocktailMenu.filter((cocktail) => selectedCocktailIds.includes(cocktail.id)).map((cocktail) => cocktail.name), [selectedCocktailIds]);
  const toggleCocktail = (id) => setSelectedCocktailIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  const applyCalculator = (brief) => {
    setCalcBrief(brief);
    document.getElementById("reserve")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const closeMenu = () => setMenuOpen(false);
  const navItems = [["manifesto", "Manifesto"], ["cocktails", "Cocktails"], ["services", "Services"], ["calculator", "Calculator"]];

  return (
    <div className="site-shell">
      <header className="site-header" data-testid="navbar-container">
        <div className="header-inner">
          <a className="brand-link" href="#top" onClick={closeMenu} data-testid="brand-logo-link" aria-label="Pop Social Events home"><BrandMark /></a>
          <nav className="desktop-nav" aria-label="Primary navigation">{navItems.map(([id, label]) => <a href={`#${id}`} key={id} data-testid={`nav-link-${id}`} onClick={closeMenu}>{label}</a>)}</nav>
          <div className="header-actions"><a className="header-cta" href="#reserve" data-testid="nav-quote-cta-button">Reserve / Get quote <ArrowUpRight size={15} /></a><button type="button" className="mobile-toggle" onClick={() => setMenuOpen((open) => !open)} data-testid="mobile-menu-toggle-button" aria-label={menuOpen ? "Close menu" : "Open menu"}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button></div>
        </div>
        {menuOpen && <nav className="mobile-panel" aria-label="Mobile navigation">{navItems.map(([id, label]) => <a href={`#${id}`} key={id} data-testid={`mobile-nav-link-${id}`} onClick={closeMenu}>{label}<ChevronRight size={16} /></a>)}<a href="#reserve" className="mobile-panel-cta" data-testid="mobile-nav-quote-link" onClick={closeMenu}>Reserve / Get quote <ArrowUpRight size={16} /></a></nav>}
      </header>

      <main>
        <Hero />
        <ImmersiveJourney />
        <Marquee />
        <Manifesto />
        <CocktailVaultNext selectedCocktails={selectedCocktailIds} onToggleQuote={toggleCocktail} />
        <PricingShowcase />
        <EventCalculator onApply={applyCalculator} onSnapshotChange={handleCalcChange} />
        <BookingForm brief={calcBrief} selectedCocktailIds={selectedCocktailIds} selectedCocktailNames={selectedCocktailNames} />
        <Proof />
        <FaqSection />
      </main>

      <footer className="site-footer" id="contact" data-testid="editorial-footer-container">
        <div className="footer-top"><SectionLabel number="10" testId="footer-section-label">The last word</SectionLabel><h2 data-testid="footer-heading">Ready to make an entrance?</h2><a className="button button-primary" href="#reserve" data-testid="footer-quote-cta">Start your event brief <ArrowUpRight size={16} /></a></div>
        <div className="footer-bottom"><div><BrandMark compact /><p>Luxury bartending + bespoke charcuterie<br />Fort Lauderdale · serving South Florida</p></div><div className="footer-links"><a href="mailto:carlos@popsocialevents.com" data-testid="footer-email-contact-link">carlos@popsocialevents.com</a><a href="https://www.instagram.com/pop_social_events/" target="_blank" rel="noreferrer" data-testid="footer-instagram-link"><Instagram size={15} /> Instagram</a><a href="https://www.facebook.com/584924824704118" target="_blank" rel="noreferrer" data-testid="footer-facebook-link">Facebook <ArrowUpRight size={15} /></a></div><span className="footer-copyright">© 2026 Pop Social Events</span></div>
      </footer>

      <BriefDrawer selectedCocktailIds={selectedCocktailIds} onRemove={toggleCocktail} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/team" element={<TeamDashboard />} />
        <Route path="/pay/:inquiryId" element={<PayDeposit />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
